import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Crown, 
  Check, 
  X, 
  Star, 
  Zap, 
  Users, 
  Calendar, 
  CreditCard, 
  Share2, 
  BarChart3,
  Building2,
  Gift,
  ArrowLeft,
  Loader2,
  Sparkles,
  Globe,
  Camera,
  Clock,
  Scissors,
  DollarSign,
  FileText,
  Wallet,
  Heart,
  Beaker,
  ShoppingCart,
  Shield,
  CheckCircle
} from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const FEATURE_LABELS = {
  // Core Features
  basic_booking: { label: "Online Booking System", icon: Calendar },
  basic_crm: { label: "CRM Dashboard", icon: Users },
  client_limit: { label: "Client Database", icon: Users },
  appointment_reminders: { label: "Appointment Reminders (SMS/Email)", icon: Calendar },
  
  // Pro Features
  loyalty_program: { label: "Loyalty Rewards Program", icon: Gift },
  calendar_sync: { label: "Google Calendar Sync", icon: Calendar },
  multiple_payments: { label: "Multiple Payment Options", icon: CreditCard },
  paypal_zelle_cashapp: { label: "PayPal, Zelle & Cash App", icon: CreditCard },
  social_sharing: { label: "Social Media Sharing", icon: Share2 },
  advanced_analytics: { label: "Advanced Analytics", icon: BarChart3 },
  priority_listing: { label: "Priority Search Listing", icon: Star },
  
  // Pro Mini-Site (NEW)
  pro_minisite: { label: "Pro Mini-Site (Custom URL)", icon: Globe, isNew: true },
  shareable_qr: { label: "Shareable QR Code Card", icon: Share2, isNew: true },
  portfolio_gallery: { label: "Portfolio Gallery (5 Images)", icon: Camera, isNew: true },
  business_hours_display: { label: "Business Hours & Policies", icon: Clock, isNew: true },
  
  // Pro Features - Roadmap 2.0 (NEW)
  smart_upsell: { label: "Smart Service Suggestions (Upsell)", icon: ShoppingCart, isNew: true },
  scalp_care_log: { label: "Scalp Care Log & Treatment History", icon: Beaker, isNew: true },
  health_profiles: { label: "Health-Aware Client Profiles", icon: Heart, isNew: true },
  
  // Elite Features - Staff Management
  staff_management: { label: "Staff Management Dashboard", icon: Users, isNew: true },
  commissioned_staff: { label: "Commissioned Staff (Barbers/Stylists)", icon: Scissors, isNew: true },
  support_staff: { label: "Support Staff (Hourly/Flat Rate)", icon: Users, isNew: true },
  payroll_tracking: { label: "Payroll & YTD Tracking", icon: DollarSign, isNew: true },
  tax_compliance: { label: "Tax Compliance (W-9 Collection)", icon: FileText, isNew: true },
  stripe_payouts: { label: "Stripe Direct Payouts", icon: CreditCard, isNew: true },
  
  // Elite Features - Salon Management
  booth_management: { label: "Booth/Chair Management", icon: Building2 },
  rent_collection: { label: "Automated Rent Collection", icon: CreditCard },
  multi_stylist_dashboard: { label: "Multi-Stylist Dashboard", icon: Users },
  team_analytics: { label: "Team Performance Analytics", icon: BarChart3 },
  
  // Elite Features - Marketing & Finance
  marketing_tools: { label: "Digital Marketing Toolkit", icon: Zap },
  accounting_dashboard: { label: "Accounting Dashboard", icon: Wallet },
  profit_loss_reports: { label: "Profit & Loss Reports", icon: BarChart3 },
  tax_summaries: { label: "Tax Summaries", icon: FileText },
  custom_branding: { label: "Custom Branding Colors", icon: Zap },
  
  // HairChair Soldiers Program (All Tiers)
  soldiers_program: { label: "HairChair Soldiers (Affiliate)", icon: Shield, isNew: true },
  soldier_command_center: { label: "Soldier Command Center", icon: BarChart3, isNew: true },
  
  // AI Features
  ai_chatbot: { label: "AI Assistant (GPT-5.2)", icon: Zap },
  ai_video_tutorials: { label: "AI Video Tutorials (Sora 2)", icon: Camera, isNew: true },
  
  // Payment Features
  zero_transaction_fees: { label: "0% Transaction Fees", icon: DollarSign },
  receipt_verification: { label: "Payment Receipt Verification", icon: CheckCircle, isNew: true }
};

const MembershipPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tiers, setTiers] = useState({});
  const [currentMembership, setCurrentMembership] = useState(null);
  const [trialEligibility, setTrialEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [startingTrial, setStartingTrial] = useState(null);
  
  // Dynamic pricing from admin settings
  const [dynamicPricing, setDynamicPricing] = useState({
    pro: { price: 29.99, trial_days: 30 },
    elite: { price: 49.99, trial_days: 30 }
  });
  
  // Promo code state
  const [promoCode, setPromoCode] = useState("");
  const [promoValidation, setPromoValidation] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [applyingPromo, setApplyingPromo] = useState(false);
  
  // Check if this is a new user onboarding
  const isWelcome = searchParams.get('welcome') === 'true';
  const userRole = searchParams.get('role') || user?.role;
  const fromTrialExpired = searchParams.get('from') === 'trial_expired';
  const returnUrl = searchParams.get('return');
  const highlightedFeature = searchParams.get('feature');

  useEffect(() => {
    fetchData();
    
    // Check for subscription success/cancel from URL
    const subscriptionStatus = searchParams.get('subscription');
    const sessionId = searchParams.get('session_id');
    
    if (subscriptionStatus === 'success' && sessionId) {
      checkSubscriptionStatus(sessionId);
    } else if (subscriptionStatus === 'cancelled') {
      toast.error("Subscription cancelled");
    }
  }, [token]);

  const fetchData = async () => {
    try {
      // Fetch tiers and pricing in parallel
      const [tiersRes, pricingRes] = await Promise.all([
        axios.get(`${API}/membership/tiers`),
        axios.get(`${API}/membership/pricing`)
      ]);
      
      setTiers(tiersRes.data.tiers || {});
      
      // Set dynamic pricing from API
      if (pricingRes.data.tiers) {
        setDynamicPricing({
          pro: pricingRes.data.tiers.pro || { price: 29.99, trial_days: 30 },
          elite: pricingRes.data.tiers.elite || { price: 49.99, trial_days: 30 }
        });
      }
      
      // Fetch current membership if logged in
      if (token) {
        const statusRes = await axios.get(`${API}/membership/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentMembership(statusRes.data);
        
        // Check trial eligibility
        try {
          const trialRes = await axios.get(`${API}/membership/trial/eligibility`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setTrialEligibility(trialRes.data);
        } catch (e) {
          console.error("Failed to check trial eligibility:", e);
        }
      }
    } catch (error) {
      console.error("Failed to fetch membership data:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkSubscriptionStatus = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/membership/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.payment_status === 'paid') {
        toast.success("🎉 Subscription activated! Welcome to Pro!");
        // If there's a return URL, redirect there after success
        if (returnUrl) {
          navigate(decodeURIComponent(returnUrl));
        } else {
          fetchData();
        }
      }
    } catch (error) {
      console.error("Failed to check subscription status:", error);
    }
  };

  // Start a free trial
  const handleStartTrial = async (tierKey) => {
    if (!token) {
      toast.error("Please login to start a trial");
      navigate("/login");
      return;
    }
    
    setStartingTrial(tierKey);
    
    try {
      // Get device fingerprint if available
      let deviceFingerprint = null;
      if (window.fpPromise) {
        try {
          const fp = await window.fpPromise;
          const result = await fp.get();
          deviceFingerprint = result.visitorId;
        } catch (e) {
          console.error("Failed to get fingerprint:", e);
        }
      }

      const res = await axios.post(
        `${API}/membership/trial/start`,
        { tier: tierKey, device_fingerprint: deviceFingerprint },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        toast.success(res.data.message);
        
        // Redirect to dashboard or return URL
        if (returnUrl) {
          navigate(decodeURIComponent(returnUrl));
        } else if (isWelcome) {
          if (userRole === 'owner') {
            navigate("/owner-dashboard");
          } else {
            navigate("/stylist");
          }
        } else {
          fetchData();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start trial");
    } finally {
      setStartingTrial(null);
    }
  };

  const handleSubscribe = async (tierKey) => {
    if (!token) {
      toast.error("Please login to subscribe");
      navigate("/login");
      return;
    }
    
    setSubscribing(tierKey);
    
    try {
      const res = await axios.post(
        `${API}/membership/subscribe/${tierKey}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = res.data.checkout_url;
      } else if (res.data.success) {
        toast.success(res.data.message);
        
        // If in welcome flow, redirect to appropriate dashboard
        if (isWelcome) {
          if (userRole === 'owner') {
            navigate("/owner-dashboard");
          } else {
            navigate("/stylist");
          }
        } else {
          fetchData();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start subscription");
    } finally {
      setSubscribing(null);
    }
  };

  // Validate promo code
  const handleValidatePromo = async () => {
    if (!promoCode.trim()) {
      toast.error("Please enter a promo code");
      return;
    }
    
    setValidatingPromo(true);
    setPromoValidation(null);
    
    try {
      const res = await axios.get(`${API}/promotions/admin/validate/${promoCode.trim()}`);
      setPromoValidation(res.data);
      
      if (res.data.valid) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      setPromoValidation({ valid: false, message: "Invalid promo code" });
      toast.error(error.response?.data?.detail || "Invalid promo code");
    } finally {
      setValidatingPromo(false);
    }
  };

  // Apply promo code to account
  const handleApplyPromo = async () => {
    if (!token) {
      toast.error("Please login to apply a promo code");
      navigate("/login");
      return;
    }
    
    if (!promoValidation?.valid) {
      toast.error("Please validate the promo code first");
      return;
    }
    
    setApplyingPromo(true);
    
    try {
      const res = await axios.post(
        `${API}/promotions/admin/apply`,
        { promo_code: promoCode.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        toast.success(res.data.message);
        setPromoCode("");
        setPromoValidation(null);
        
        // Refresh membership data
        fetchData();
        
        // Redirect after short delay
        setTimeout(() => {
          if (user?.role === 'owner') {
            navigate("/owner-dashboard");
          } else if (user?.role === 'stylist') {
            navigate("/stylist");
          } else {
            navigate("/dashboard");
          }
        }, 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply promo code");
    } finally {
      setApplyingPromo(false);
    }
  };
  
  // Handle "Continue with Free" for welcome flow
  const handleContinueFree = () => {
    if (userRole === 'owner') {
      navigate("/owner-dashboard");
    } else {
      navigate("/stylist");
    }
  };

  const getFeatureValue = (tierKey, featureKey) => {
    const tier = tiers[tierKey];
    if (!tier) return false;
    
    const value = tier.features[featureKey];
    
    if (featureKey === "client_limit") {
      return value === -1 ? "Unlimited" : `Up to ${value}`;
    }
    
    return value;
  };

  const isCurrentTier = (tierKey) => {
    return currentMembership?.membership?.tier === tierKey;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-stone-900 to-black">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-900 to-black py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Back Button - only show if not welcome flow */}
        {!isWelcome && (
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          {/* Welcome message for new users */}
          {isWelcome && (
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">
                🎉 Welcome to MyHairChair, {user?.name || 'Pro'}!
              </h2>
              <p className="text-gray-400">
                Your account is ready. Choose a plan to unlock your full potential.
              </p>
            </div>
          )}
          
          {/* Introductory Pricing Banner */}
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-purple-500/20 border border-amber-500/30 px-4 py-2 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 text-sm font-medium">
              🎉 Introductory Pricing — Limited Time Offer!
            </span>
          </div>
          
          <div className="inline-flex items-center gap-2 bg-stone-800 border border-stone-600 px-4 py-2 rounded-full mb-4 ml-2">
            <span className="text-white text-sm font-medium">
              Barber / Stylist Plans
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            {isWelcome ? (
              <>Start Growing Your <span className="bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">Business</span></>
            ) : (
              <>Choose Your <span className="bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">Plan</span></>
            )}
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            {isWelcome ? (
              userRole === 'owner' 
                ? "As a Salon/Shop Owner, you can manage your team, collect rent, and grow your business with our Elite plan."
                : "Unlock booking, payments, social sharing, and more. 30-day FREE trial on all plans!"
            ) : (
              "Unlock powerful features to grow your business. Start with a 30-day free trial!"
            )}
          </p>
          
          {currentMembership && !isWelcome && (
            <div className="mt-4 inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 px-4 py-2 rounded-full">
              <Check className="w-4 h-4 text-green-400" />
              <span className="text-green-400 text-sm">
                Current plan: <strong>{tiers[currentMembership.membership?.tier]?.name || 'Pro'}</strong>
              </span>
            </div>
          )}
        </div>

        {/* 30-Day Free Trial Banner */}
        {!currentMembership?.membership?.tier || currentMembership?.membership?.tier === 'free' ? (
          <div className="mb-8 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-2xl p-6 text-center">
            <div className="flex items-center justify-center gap-3 mb-2">
              <Gift className="w-6 h-6 text-green-400" />
              <h3 className="text-xl font-bold text-white">30-Day FREE Trial on All Plans!</h3>
              <Gift className="w-6 h-6 text-green-400" />
            </div>
            <p className="text-green-300 mb-3">
              Try Pro or Elite features FREE for 30 days. No credit card required to start!
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-300">
              <span className="flex items-center gap-1"><Check className="w-4 h-4 text-green-400" /> Full Feature Access</span>
              <span className="flex items-center gap-1"><Check className="w-4 h-4 text-green-400" /> Cancel Anytime</span>
              <span className="flex items-center gap-1"><Check className="w-4 h-4 text-green-400" /> No Commitment</span>
            </div>
          </div>
        ) : null}

        {/* Promo Code Section */}
        <div className="mb-8 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-2xl p-6">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                <Gift className="w-5 h-5 text-purple-400" />
                <h3 className="text-lg font-bold text-white">Have a Promo Code?</h3>
              </div>
              <p className="text-purple-300/80 text-sm">
                Enter your promotional code to unlock special offers or free premium access!
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <div className="relative flex-1 sm:min-w-[200px]">
                <Input
                  placeholder="Enter promo code"
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value.toUpperCase());
                    setPromoValidation(null);
                  }}
                  className="bg-stone-800 border-stone-600 text-white placeholder:text-gray-500 uppercase"
                  data-testid="promo-code-input"
                />
                {promoValidation?.valid && (
                  <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-400" />
                )}
              </div>
              
              {!promoValidation?.valid ? (
                <Button
                  onClick={handleValidatePromo}
                  disabled={validatingPromo || !promoCode.trim()}
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                  data-testid="validate-promo-btn"
                >
                  {validatingPromo ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking...</>
                  ) : (
                    "Apply Code"
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handleApplyPromo}
                  disabled={applyingPromo}
                  className="bg-green-500 hover:bg-green-600 text-white"
                  data-testid="apply-promo-btn"
                >
                  {applyingPromo ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Activating...</>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Activate {promoValidation.duration_days} Days Free
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
          
          {/* Promo validation result */}
          {promoValidation && (
            <div className={`mt-4 p-3 rounded-lg ${promoValidation.valid ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
              {promoValidation.valid ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-green-400 font-semibold">{promoValidation.name}</p>
                    <p className="text-green-300/80 text-sm">
                      {promoValidation.duration_days} days of FREE {promoValidation.tier?.toUpperCase()} access!
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-red-400 text-sm flex items-center gap-2">
                  <X className="w-4 h-4" />
                  {promoValidation.message}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Pricing Cards - 2 TIERS ONLY */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
          {/* Pro Tier */}
          <Card className={`bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border-amber-500/50 relative ${isCurrentTier('pro') ? 'ring-2 ring-green-500' : ''}`}>
            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-400 to-yellow-500 text-black">
              {isCurrentTier('pro') ? 'Current Plan' : '🔥 Most Popular'}
            </Badge>
            <div className="absolute top-4 right-4">
              <Badge className="bg-green-500 text-white text-xs">30-DAY FREE TRIAL</Badge>
            </div>
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-white text-2xl flex items-center justify-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-400" />
                Pro
              </CardTitle>
              <CardDescription className="text-amber-400/80">For barbers & stylists</CardDescription>
              <div className="mt-4">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-4xl font-bold text-white">$29.99</span>
                  <span className="text-gray-400">/mo</span>
                </div>
                <p className="text-green-400 text-sm font-medium">30-DAY FREE TRIAL!</p>
                <p className="text-gray-500 text-xs mt-1">No credit card required to start</p>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <span className="text-gray-300"><strong>Unlimited</strong> clients</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <span className="text-gray-300">CRM Dashboard</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <span className="text-gray-300">Appointment Reminders (SMS/Email)</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <span className="text-gray-300">Pro Mini-Site (Custom URL)</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <span className="text-gray-300">Shareable QR Code Cards</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <span className="text-gray-300">All Payment Methods</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <span className="text-gray-300">Loyalty Rewards Program</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <span className="text-gray-300">Google Calendar Sync</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <span className="text-gray-300">Advanced Analytics</span>
                </li>
              </ul>
            </CardContent>
            <CardFooter className="flex flex-col gap-2">
              {/* Trial button - shown if eligible */}
              {trialEligibility?.eligible && !isCurrentTier('pro') && (
                <Button 
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold hover:opacity-90"
                  disabled={startingTrial === 'pro'}
                  onClick={() => handleStartTrial('pro')}
                  data-testid="start-pro-trial-btn"
                >
                  {startingTrial === 'pro' ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting Trial...</>
                  ) : (
                    <>✨ Start 30-Day Free Trial</>
                  )}
                </Button>
              )}
              <Button 
                className="w-full bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-semibold hover:opacity-90"
                disabled={isCurrentTier('pro') || subscribing === 'pro'}
                onClick={() => handleSubscribe('pro')}
              >
                {subscribing === 'pro' ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                ) : isCurrentTier('pro') ? (
                  'Current Plan'
                ) : (
                  'Subscribe Now - $29.99/mo'
                )}
              </Button>
            </CardFooter>
          </Card>

          {/* Elite Tier */}
          <Card className={`bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/50 relative ${isCurrentTier('elite') ? 'ring-2 ring-green-500' : ''}`}>
            {isCurrentTier('elite') ? (
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-white">
                Current Plan
              </Badge>
            ) : (
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                👑 Best for Owners
              </Badge>
            )}
            <div className="absolute top-4 right-4">
              <Badge className="bg-green-500 text-white text-xs">30-DAY FREE TRIAL</Badge>
            </div>
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-white text-2xl flex items-center justify-center gap-2">
                <Crown className="w-5 h-5 text-purple-400" />
                Elite
              </CardTitle>
              <CardDescription className="text-purple-400/80">For Salon & Shop Owners</CardDescription>
              <div className="mt-4">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-4xl font-bold text-white">$49.99</span>
                  <span className="text-gray-400">/mo</span>
                </div>
                <p className="text-green-400 text-sm font-medium">30-DAY FREE TRIAL!</p>
                <p className="text-gray-500 text-xs mt-1">No credit card required to start</p>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span className="text-gray-300"><strong>Everything in Pro</strong></span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span className="text-gray-300">Staff Management Dashboard</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span className="text-gray-300">Commission Tracking</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span className="text-gray-300">Payroll & W-9 Compliance</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span className="text-gray-300">Booth/Chair Rent Collection</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span className="text-gray-300">Team Analytics</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span className="text-gray-300">Full Reports & P&L</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span className="text-gray-300">Custom Branding</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span className="text-gray-300">0% Platform Fees</span>
                </li>
              </ul>
            </CardContent>
            <CardFooter className="flex flex-col gap-2">
              {/* Trial button - shown if eligible and is owner */}
              {trialEligibility?.eligible && !isCurrentTier('elite') && user?.role === 'owner' && (
                <Button 
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold hover:opacity-90"
                  disabled={startingTrial === 'elite'}
                  onClick={() => handleStartTrial('elite')}
                  data-testid="start-elite-trial-btn"
                >
                  {startingTrial === 'elite' ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting Trial...</>
                  ) : (
                    <>✨ Start 30-Day Free Trial</>
                  )}
                </Button>
              )}
              <Button 
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold hover:opacity-90"
                disabled={isCurrentTier('elite') || subscribing === 'elite' || user?.role !== 'owner'}
                onClick={() => handleSubscribe('elite')}
              >
                {subscribing === 'elite' ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                ) : isCurrentTier('elite') ? (
                  'Current Plan'
                ) : user?.role !== 'owner' ? (
                  'Owners Only'
                ) : (
                  'Subscribe Now - $49.99/mo'
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-6">
                <h3 className="text-white font-semibold mb-2">How does the 30-day free trial work?</h3>
                <p className="text-gray-400 text-sm">Choose Pro or Elite and enjoy full access for 30 days. No credit card required to start! After the trial, you&apos;ll be charged the monthly rate. Cancel anytime during the trial at no cost.</p>
              </CardContent>
            </Card>
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-6">
                <h3 className="text-white font-semibold mb-2">What happens when my trial ends?</h3>
                <p className="text-gray-400 text-sm">Your subscription will begin at the regular monthly rate. You can cancel anytime. Your data and settings are always preserved.</p>
              </CardContent>
            </Card>
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-6">
                <h3 className="text-white font-semibold mb-2">Can I cancel anytime?</h3>
                <p className="text-gray-400 text-sm">Yes! You can cancel your subscription at any time. Your features will remain active until the end of your billing period.</p>
              </CardContent>
            </Card>
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-6">
                <h3 className="text-white font-semibold mb-2">What payment methods do you accept?</h3>
                <p className="text-gray-400 text-sm">We accept all major credit cards through Stripe&apos;s secure payment processing.</p>
              </CardContent>
            </Card>
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-6">
                <h3 className="text-white font-semibold mb-2">Is Elite really only for owners?</h3>
                <p className="text-gray-400 text-sm">Yes, Elite tier includes booth management and rent collection features designed specifically for salon and barbershop owners who manage multiple chairs or stylists.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipPage;
